import { useState } from 'react';

import { FeatureCard, PostCard, PriceCard } from '../components';

import {
  Amazon,
  DigitalOcean,
  Facebook,
  Lightning,
  MinusCircle,
  PlusCircle,
  Quote,
  Star,
  Tesla,
} from '../icons';

import { benefits, features, blogPosts, plans, faq } from '../copies';

const Home = ({ onClickDemo = () => null }) => {
  const [selectedQ, setSelectedQ] = useState(-1);

  return (
    <>
      {/* Hero section */}
      <section className="lg:pt-4 -mx-6 md:-mx-10 bg-gray-50 dark:bg-gray-900 dark:text-white">
        <div className="lg:px-10">
          <div
            className="lg:container lg:max-w-screen-xl mx-auto bg-cover bg-no-repeat shadow-lg lg:rounded-2xl"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/assets/teamwork.jpg)`,
            }}
          >
            <div className="px-6 bg-primary-600 bg-opacity-90 text-white py-16 sm:py-24 lg:py-40 lg:rounded-2xl">
              {/* Headlines + CTA */}
              <div className="space-y-8">
                <h1 className="text-5xl sm:text-7xl text-center text-shadow font-extrabold">
                  <span className="sm:block inline">Jumpstart your next </span>
                  <span className="sm:block inline text-primary-200">
                    SaaS product
                  </span>
                </h1>
                <p className="text-2xl text-center text-shadow max-w-3xl mx-auto text-gray-200">
                  Lorem, ipsum dolor sit amet consectetur adipisicing elit.
                  Incidunt optio officiis dolore earum error eaque perferendis
                  laudantium sed.
                </p>
                <div className="flex justify-center flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 max-w-xs mx-auto">
                  <a
                    href="#pricing"
                    className="flex-shrink-0 px-8 py-4 tracking-wide font-medium bg-white text-primary-600 rounded-lg focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-50 transition-colors flex items-center justify-center"
                  >
                    <Lightning className="w-6 h-6 mr-1 flex-shrink-0" />
                    Get started
                  </a>
                  <button
                    className="flex-shrink-0 px-8 py-4 tracking-wide font-medium border-2 border-white bg-transparent text-white rounded-lg focus:outline-none focus:ring-4 focus:ring-primary-600 focus:ring-opacity-50 transition-colors"
                    onClick={onClickDemo}
                  >
                    Live demo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logo section */}
      <section className="py-12 -mx-6 md:-mx-10 bg-gray-50 dark:bg-gray-900 dark:text-white">
        <div className="px-6 md:px-10">
          <div className="container max-w-screen-xl mx-auto space-y-6">
            <p className="uppercase text-gray-500 text-center font-medium">
              Trusted by top-leading companies
            </p>
            <div className="space-y-8 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-8 md:grid-cols-4">
              {/* Facebook */}
              <div className="flex items-center justify-center">
                <Facebook className="h-12 text-gray-500 fill-current" />
              </div>

              {/* Tesla */}
              <div className="flex items-center justify-center">
                <Tesla className="h-5 mt-1 text-gray-500 fill-current" />
              </div>

              {/* Digital Ocean */}
              <div className="flex items-center justify-center">
                <DigitalOcean className="h-10 text-gray-500 fill-current" />
              </div>

              {/* Amazon */}
              <div className="flex items-center justify-center">
                <Amazon className="h-8 mt-2 text-gray-500 fill-current" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits section */}
      <section className="py-24 container max-w-screen-xl mx-auto">
        <div className="space-y-24">
          {benefits.map(({ title, description, image, icon: Icon }, index) => (
            <div
              className="space-y-12 lg:space-y-0 lg:grid lg:grid-cols-2"
              key={title}
            >
              <div
                className={`col-span-2 lg:col-span-1 space-y-4 flex flex-col justify-center items-center lg:items-start text-center lg:text-left ${
                  index % 2 === 0 ? 'lg:mr-10' : 'lg:ml-10'
                }`}
              >
                <span className="p-3 rounded-md bg-gradient-to-br from-secondary-500 to-primary-600 max-w-max">
                  {Icon ? <Icon className="w-6 h-6 text-white" /> : null}
                </span>
                <h2 className="font-extrabold text-3xl sm:text-4xl">{title}</h2>
                <p className="leading-7 text-gray-500 text-lg">{description}</p>
                <div>
                  <a
                    href="#pricing"
                    className="flex-shrink-0 px-8 py-4 tracking-wide font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg focus:outline-none focus:ring-4 focus:ring-primary-600 focus:ring-opacity-50 transition-colors"
                  >
                    Get started
                  </a>
                </div>
              </div>
              <div
                className={`col-span-2 md:col-span-1 shadow-xl rounded-xl bg-cover bg-no-repeat bg-center h-80 lg:h-128 lg:scale-x-125 lg:transform ${
                  index % 2 === 0
                    ? 'order-last lg:origin-left lg:translate-x-10 lg:rounded-r-none xl:rounded-r-xl'
                    : 'md:order-first origin-right lg:-translate-x-10 lg:rounded-l-none xl:rounded-l-xl'
                }`}
                style={{
                  backgroundImage: `url(${image})`,
                }}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Features section */}
      <section className="py-24 container max-w-screen-xl mx-auto space-y-24">
        <div className="text-center">
          <p className="text-primary-600 text-lg font-semibold tracking-wide uppercase">
            Build faster
          </p>
          <h2 className="font-extrabold text-4xl sm:text-5xl mt-2">
            Everything your app needs and more
          </h2>
          <p className="text-2xl text-center max-w-3xl mx-auto text-gray-500 mt-6">
            Lorem, ipsum dolor sit amet consectetur adipisicing elit. Incidunt
            optio officiis dolore earum error eaque perferendis laudantium sed.
          </p>
        </div>

        <div className="space-y-16 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-x-8 sm:gap-y-14 max-w-md sm:max-w-3xl lg:max-w-7xl mx-auto">
          {features.map(feature => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </div>
      </section>

      {/* Testimonial section */}
      <section className="-mx-6 md:-mx-10 xl:my-24 bg-gradient-to-tr from-secondary-500 to-primary-600 text-white">
        <div className="px-6 md:px-10">
          <div className="container max-w-screen-xl mx-auto flex flex-col sm:flex-row justify-center items-center py-12 sm:py-0 space-y-1 sm:space-y-0 sm:space-x-12 xl:space-x-16">
            <div
              className="shadow-xl rounded-full sm:rounded-xl bg-cover bg-center bg-no-repeat w-48 sm:w-80 lg:w-1/3 h-48 sm:h-96 sm:my-8 xl:h-128 xl:-my-8"
              style={{
                backgroundImage: `url(${process.env.PUBLIC_URL}/assets/user.jpg)`,
              }}
            />
            <div className="text-center sm:text-left sm:w-1/2 lg:w-2/3">
              <blockquote className="space-y-8 relative">
                <Quote className="w-16 h-16 opacity-10 absolute -top-8 -left-2 sm:-left-4" />
                <p className="text-xl lg:text-2xl xl:text-3xl leading-snug">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                  do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                  Convallis posuere morbi leo urna molestie at elementum eu
                  facilisis.
                </p>
                <div className="space-y-1 tracking-wide">
                  <p className="text-lg">Judith Lord</p>
                  <p className="opacity-80">CEO at AlterClass</p>
                </div>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Blog section */}
      <section className="py-24 container max-w-screen-xl mx-auto space-y-24">
        <div className="text-center">
          <p className="text-primary-600 text-lg font-semibold tracking-wide uppercase">
            Learn
          </p>
          <h2 className="font-extrabold text-4xl sm:text-5xl mt-2">
            Helpful resources
          </h2>
          <p className="text-2xl text-center max-w-3xl mx-auto text-gray-500 mt-6">
            Lorem, ipsum dolor sit amet consectetur adipisicing elit. Incidunt
            optio officiis dolore earum error eaque perferendis laudantium sed.
          </p>
        </div>

        <div className="space-y-12 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-8">
          {blogPosts.map(post => (
            <div className="max-w-lg mx-auto" key={post.title}>
              <PostCard {...post} />
            </div>
          ))}
        </div>
      </section>

      {/* Pricing section */}
      <section
        id="pricing"
        className="py-24 container max-w-screen-xl mx-auto space-y-24"
      >
        <div className="text-center">
          <p className="text-primary-600 text-lg font-semibold tracking-wide uppercase">
            Get started now
          </p>
          <h2 className="font-extrabold text-4xl sm:text-5xl mt-2">
            Simple and transparent pricing
          </h2>
          <p className="text-2xl text-center max-w-3xl mx-auto text-gray-500 mt-6">
            Lorem, ipsum dolor sit amet consectetur adipisicing elit. Incidunt
            optio officiis dolore earum error eaque perferendis laudantium sed.
          </p>
        </div>

        <div className="space-y-16 lg:space-y-0 lg:grid lg:grid-cols-3">
          {plans.map(plan => (
            <div
              key={plan.name}
              className={`${
                plan.featured
                  ? 'order-1 lg:order-none  lg:transform lg:scale-110 lg:z-10'
                  : null
              } w-full max-w-lg mx-auto`}
            >
              <PriceCard {...plan} />
            </div>
          ))}
        </div>

        <div className="bg-yellow-50 rounded-lg border border-yellow-200 py-4 lg:py-8 px-4 md:px-12 w-full max-w-lg lg:max-w-max mx-auto text-center md:text-left flex flex-col md:flex-row items-center justify-center md:space-x-4 space-y-4 md:space-y-0 lg:space-x-8">
          <div>
            <p className="tracking-wide uppercase font-bold text-gray-700 text-lg lg:text-xl">
              4.9 Overall rating
            </p>
            <p className="text-base lg:text-lg text-gray-600">
              Serving 2500 companies worldwide
            </p>
          </div>
          <div className="text-yellow-400 flex space-x-1">
            {[...new Array(5)].map((_, i) => (
              <Star className="w-7 h-7" key={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ section */}
      <section className="py-24 container max-w-screen-xl mx-auto space-y-24">
        <div className="text-center">
          <p className="text-primary-600 text-lg font-semibold tracking-wide uppercase">
            Ask us anything
          </p>
          <h2 className="font-extrabold text-4xl sm:text-5xl mt-2">
            Frequently Asked Questions
          </h2>
          <p className="text-2xl text-center max-w-3xl mx-auto text-gray-500 mt-6">
            Lorem, ipsum dolor sit amet consectetur adipisicing elit. Incidunt
            optio officiis dolore earum error eaque perferendis laudantium sed.
          </p>
        </div>

        <div>
          <ul className="divide-y divide-gray-300 md:text-lg">
            {faq.map(({ question, answer }, index) => (
              <li key={index}>
                {/* Question */}
                <button
                  className="py-4 font-bold focus:outline-none hover:text-primary-700 w-full text-left flex items-center justify-between transition-colors space-x-4"
                  onClick={() =>
                    index === selectedQ ? setSelectedQ(-1) : setSelectedQ(index)
                  }
                >
                  <span>{question}</span>
                  {index === selectedQ ? (
                    <MinusCircle className="flex-shrink-0 w-6 h-6 text-primary-600" />
                  ) : (
                    <PlusCircle className="flex-shrink-0 w-6 h-6 text-primary-600" />
                  )}
                </button>

                {/* Answer */}
                {index === selectedQ ? (
                  <div className="pr-6 pb-4 text-gray-500">
                    <p>{answer}</p>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};

export default Home;
